import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Chip } from '@mui/material';

import {
  ListItemElement,
  SelectItemsDialogFC,
  SelectItemsDialogFCProps,
} from './SelectItemsDialogFC';

export interface ItemsListFCTestIds {
  addButton: string;
}

export interface ItemsListFCProps {
  strings: {
    listComponent: Record<'addLabel', string>;
    dialogComponent: SelectItemsDialogFCProps['strings'];
    viewMore?: (count: number) => string;
  };
  selectedItems: string[];
  indeterminateTags?: string[];
  itemsList: ListItemElement[];
  onItemListToggle: () => void;
  isDefaultDialogOpen?: boolean;
  onSelectedValuesChange: (selectedValues: string[], indeterminate?: string[]) => void;
  shouldShowImages?: boolean;
  isBulkTagsEditMode?: boolean;
  maxSelectedItemsChips?: number;
  testIds?: ItemsListFCTestIds;
}

interface ListItemFCProps {
  id: string;
  label: string;
  onDeselect: (id: string) => void;
}

const ListItemFC: React.FC<ListItemFCProps> = memo(({ id, label, onDeselect }) => {
  const onDeleteHandler = useCallback(() => {
    onDeselect(id);
  }, [id, onDeselect]);

  return (
    <Box mr={1} mb={1}>
      <Chip label={label} onDelete={onDeleteHandler} color="secondary" />
    </Box>
  );
});

export const ItemsListFC = memo(function ItemsListFC({
  strings,
  itemsList,
  selectedItems,
  indeterminateTags,
  onItemListToggle,
  onSelectedValuesChange,
  shouldShowImages,
  isDefaultDialogOpen = false,
  isBulkTagsEditMode = false,
  maxSelectedItemsChips,
  testIds,
}: ItemsListFCProps) {
  const [isDialogOpen, setDialogOpen] = useState<boolean>(isDefaultDialogOpen);
  const [selectedItemsList, setSelectedItemsList] = useState<string[]>(selectedItems);
  const [sortedItemList, setSortedItemList] = useState<ListItemElement[]>(itemsList);

  const onClickAddItemHandler = useCallback(() => {
    setDialogOpen(true);
    onItemListToggle();
  }, [onItemListToggle]);

  const onClickCancelHandler = useCallback(() => {
    setDialogOpen(false);
    onItemListToggle();
  }, [onItemListToggle]);

  const onAddItemsHandler = useCallback(
    (selectedValues: string[], indeterminate?: string[]) => {
      if (isBulkTagsEditMode) {
        onSelectedValuesChange(selectedValues, indeterminate);
      } else {
        onSelectedValuesChange(selectedValues);
      }
      setSelectedItemsList(selectedValues);
      setDialogOpen(false);
    },
    [onSelectedValuesChange, isBulkTagsEditMode],
  );

  const onItemDeselectHandler = useCallback(
    (id: string) => {
      setSelectedItemsList((prevArray) => {
        const currentSelectedItemsSet = new Set(prevArray);
        currentSelectedItemsSet.delete(id);
        const currentSelectedItems = Array.from(currentSelectedItemsSet);
        onSelectedValuesChange(currentSelectedItems);
        return currentSelectedItems;
      });
    },
    [onSelectedValuesChange],
  );

  const selectedItemsChips = useMemo(() => {
    const visibleSelectedItemsList = maxSelectedItemsChips
      ? selectedItemsList.slice(0, maxSelectedItemsChips)
      : selectedItemsList;
    const selectedList = visibleSelectedItemsList.map((item) => {
      const itemInfo = itemsList.find((listItem) => listItem.id === item);
      if (!itemInfo) {
        return null;
      }
      return (
        <ListItemFC
          key={item}
          id={item}
          label={itemInfo.label}
          onDeselect={onItemDeselectHandler}
        />
      );
    });
    const selectedListIds: string[] = selectedList.map((selected) => selected?.key as string);
    // Render first default selected Items
    const sortedList = itemsList.sort(
      (a, b) => selectedListIds.indexOf(b.id) - selectedListIds.indexOf(a.id),
    );
    setSortedItemList(sortedList);

    return selectedList;
  }, [itemsList, maxSelectedItemsChips, onItemDeselectHandler, selectedItemsList]);

  useEffect(() => {
    setSelectedItemsList(selectedItems);
  }, [selectedItems]);

  return (
    <>
      {!isBulkTagsEditMode && (
        <Box display="flex" flexWrap="wrap">
          <Box mb={1} mr={1}>
            <Chip
              variant="outlined"
              icon={<AddIcon />}
              label={strings.listComponent.addLabel}
              clickable
              onClick={onClickAddItemHandler}
              data-testid={testIds?.addButton}
            />
          </Box>
          {selectedItemsChips}
          {maxSelectedItemsChips &&
            strings.viewMore &&
            selectedItems.length > maxSelectedItemsChips && (
              <Chip
                color="secondary"
                label={strings.viewMore(selectedItems.length - maxSelectedItemsChips)}
                clickable
                onClick={onClickAddItemHandler}
              />
            )}
        </Box>
      )}
      <SelectItemsDialogFC
        open={isDialogOpen}
        strings={strings.dialogComponent}
        indeterminateTags={indeterminateTags}
        itemList={sortedItemList}
        selectedItemList={selectedItemsList}
        onClickCancel={onClickCancelHandler}
        onClickAddItems={onAddItemsHandler}
        isBulkTagsEditMode={isBulkTagsEditMode}
        shouldShowImages={shouldShowImages ?? false}
      />
    </>
  );
});
